import './RetailOpsChannels.scss';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { MdAdd } from 'react-icons/md';
import {
  AmazonAccountRow,
  COUNTRY_TO_MARKETPLACE_ID
} from '@blisspointmedia/bpm-types/dist/AmazonSPAPI';
import { Button } from '@Components/Button';
import { Background, Design, Intent, Placement, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { InfoTooltip } from '@Components/InfoTooltip';
import { ModalEditWidget } from '@Components/ModalEditWidget';
import {
  CustomModalEditWidgetHeader,
  ModalEditWidgetHandle,
  SaveChangesInput,
  TableRow
} from '@Components/ModalEditWidget/modal-edit-constants';
import { Page } from '@Components/Page';
import { WidgetContainer } from '@Components/WidgetContainer';
import { useSetError } from '@src/redux/modals';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { EditGrepsrCredentialsModal } from './EditGrepsrCredentialsModal';
import { EditSkaiPacvueIdsModal } from './EditSkaiPacvueIdsModal';
import {
  checkSellingPartnerAuthorization,
  updateAspMappings,
  useSellingPartnerAccounts,
  useSkaiPacvueAccountOptions
} from './use-retail-ops-channels';

const ROW_BASE_HEIGHT = 40;
const ROW_LINE_HEIGHT = 24;
const IDS_PER_LINE = 3;

const estimateRowHeight = (row?: AmazonAccountRow): number => {
  const idCount = row?.skai_pacvue_account_ids?.length ?? 0;
  const lines = Math.max(1, Math.ceil(idCount / IDS_PER_LINE));
  return ROW_BASE_HEIGHT + (lines - 1) * ROW_LINE_HEIGHT;
};

const RetailOpsModal = {
  SKAI_PACVUE: 'skai_pacvue',
  GREPSR: 'grepsr'
} as const;
type RetailOpsModalType = (typeof RetailOpsModal)[keyof typeof RetailOpsModal];

const NoAccountsRow = () => (
  <div
    style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      fontSize: '24px'
    }}
  >
    There are no existing auth connections for this client. Add one by submitting and authenticating
    above.
  </div>
);

export const RetailOpsChannels = () => {
  const setError = useSetError();
  const companyInfo = useCompanyInfo();
  const { cid: company } = companyInfo;
  const [applicationType, setApplicationType] = useState('');
  const [brand, setBrand] = useState('');
  const [marketplace, setMarketplace] = useState('');
  const [loading, setLoading] = useState<boolean>(false);

  const [redirectUrl, setRedirectUrl] = useState<string>('');

  const {
    data: accountRows,
    isLoading: accountRowsLoading,
    error: accountRowsError,
    refetch: refetchAmazonAccountRows
  } = useSellingPartnerAccounts(company);

  const {
    data: skaiPacvueOptions,
    isLoading: skaiPacvueOptionsLoading,
    error: skaiPacvueOptionsError
  } = useSkaiPacvueAccountOptions(company);

  const widgetRef = useRef<ModalEditWidgetHandle<AmazonAccountRow>>(null);
  const [editingRow, setEditingRow] = useState<TableRow<AmazonAccountRow> | undefined>();
  const [activeModal, setActiveModal] = useState<RetailOpsModalType>();

  useEffect(() => {
    if (accountRowsError) {
      setError({
        message: `Failed to fetch selling partner accounts: ${accountRowsError.message}`,
        reportError: accountRowsError
      });
    }
  }, [accountRowsError, setError]);

  useEffect(() => {
    if (skaiPacvueOptionsError) {
      setError({
        message: `Failed to fetch Skai/Pacvue account options: ${skaiPacvueOptionsError.message}`,
        reportError: skaiPacvueOptionsError
      });
    }
  }, [skaiPacvueOptionsError, setError]);

  const checkAuthorization = useCallback(async () => {
    try {
      const data = await checkSellingPartnerAuthorization({
        company,
        applicationType,
        marketplace,
        brand
      });
      if (data.substring(0, 5) === 'https') {
        setRedirectUrl(data);
      } else {
        setError({
          intent: Intent.SUCCESS,
          message: data
        });
      }
    } catch (e) {
      setError({
        message: `Failed to check SPAPI authorization: ${e.message}`,
        reportError: e
      });
    }
  }, [company, applicationType, marketplace, brand, setError]);

  const signInToAmazon = useCallback(() => {
    try {
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
    } catch (e) {
      setError({
        message: `Failed redirect to Amazon: ${e.message}`,
        reportError: e
      });
    }
  }, [redirectUrl, setError]);

  const oauthProcess = useCallback(async () => {
    setLoading(true);
    await checkAuthorization();
    setLoading(false);
  }, [checkAuthorization]);

  useEffect(() => {
    if (redirectUrl) {
      signInToAmazon();
    }
  }, [redirectUrl, signInToAmazon]);

  const marketplaceOptions = useMemo(
    () =>
      Object.keys(COUNTRY_TO_MARKETPLACE_ID).map(key => ({
        label: key,
        value: COUNTRY_TO_MARKETPLACE_ID[key]
      })),
    []
  );

  const openEditModal = useCallback((row: TableRow<AmazonAccountRow>) => {
    setEditingRow(row);
    setActiveModal(RetailOpsModal.SKAI_PACVUE);
  }, []);

  const openGrepsrModal = useCallback((row: TableRow<AmazonAccountRow>, e: React.MouseEvent) => {
    // Stop the click from bubbling to the row's onRowClick (openEditModal) --
    // otherwise this button click would also open the Skai/Pacvue modal.
    e.stopPropagation();
    setEditingRow(row);
    setActiveModal(RetailOpsModal.GREPSR);
  }, []);

  const tableHeaders: CustomModalEditWidgetHeader[] = useMemo(
    () => [
      { label: 'Account Type', field: 'account_type', width: 120 },
      { label: 'Account ID', field: 'account_id', width: 120 },
      { label: 'Account Name', field: 'account_name', flex: 1 },
      { label: 'Secret Path', field: 'secret_path', flex: 1 },
      { label: 'Marketplace ID', field: 'marketplace_id', width: 200 },
      { label: 'Marketplace', field: 'marketplace', width: 220 },
      {
        label: 'Skai / Pacvue IDs',
        field: 'skai_pacvue_account_ids',
        width: 116,
        renderer: (row: TableRow<AmazonAccountRow>) => {
          if (row.skai_pacvue_account_ids.length === 0) {
            return (
              <div className='cell-centered'>
                <Button leadingElement={<MdAdd />} onClick={() => openEditModal(row)}>
                  Add IDs
                </Button>
              </div>
            );
          }
          return (
            <div className='id-pills cell-centered'>
              {row.skai_pacvue_account_ids.map(id => (
                <OverlayTrigger
                  key={id}
                  placement='top'
                  overlay={<Tooltip id={`${id}-pill-tooltip`}>{id}</Tooltip>}
                >
                  <span className='id-pill'>{id}</span>
                </OverlayTrigger>
              ))}
            </div>
          );
        }
      },
      {
        label: 'Grepsr',
        field: 'grespr_id',
        width: 160,
        renderer: (row: TableRow<AmazonAccountRow>) => {
          if (row.grespr_id) {
            return (
              <div className='cell-centered'>
                <span className='id-pill'>{row.grespr_id}</span>
              </div>
            );
          }
          return (
            <div className='cell-centered'>
              <Button leadingElement={<MdAdd />} onClick={e => openGrepsrModal(row, e)}>
                Set Up Grepsr
              </Button>
            </div>
          );
        }
      }
    ],
    [openEditModal, openGrepsrModal]
  );

  const handleSaveSkaiPacvueIds = useCallback(
    async ({ modifiedData, cleanup }: SaveChangesInput<AmazonAccountRow>) => {
      try {
        await updateAspMappings({
          company,
          updates: modifiedData.map(row => ({
            aspId: row.account_id,
            skaiPacvueAccountIds: row.skai_pacvue_account_ids
          }))
        });
        cleanup();
        await refetchAmazonAccountRows();
      } catch (e) {
        setError({
          message: `Failed to update Skai/Pacvue IDs: ${e.message}`,
          reportError: e
        });
      }
    },
    [company, refetchAmazonAccountRows, setError]
  );

  return (
    <Page minHeight='600px' pageType='Retail Ops Channels' title='Retail Ops Channels'>
      <div className='retail-ops-channel-page'>
        <div className='retail-ops-controls'>
          <WidgetContainer
            className='amazon-oauth-container'
            header='Amazon SPAPI OAuth'
            background={Background.DARK}
          >
            <div className='controls'>
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                size={Size.LG}
                onChange={option => setApplicationType(option)}
                options={[
                  { label: 'Amazon Selling Partner - Vendor', value: 'vendor' },
                  { label: 'Amazon Selling Partner - Seller', value: 'seller' }
                ]}
                placeholder='Selling Partner Type'
                value={applicationType}
              />
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                size={Size.LG}
                onChange={option => setMarketplace(option)}
                options={marketplaceOptions}
                placeholder='Marketplace'
                value={marketplace}
              />
              <div className='brand-input'>
                <Form.Control
                  size='lg'
                  onChange={e => setBrand(e.target.value)}
                  onBlur={() => setBrand(curr => curr.trim().toLocaleLowerCase().replace(/ /g, ''))}
                  value={brand}
                  placeholder='Brand'
                />
                <InfoTooltip size={Size.LG} design={Design.NEUTRAL}>
                  <div>
                    <p>
                      If applicable, this brand identifier helps distinguish multiple SPAPI
                      authorizations for the same company + selling partner type + region
                      combination.
                    </p>
                    <p>
                      If not relevant, just use the company ID, which is the same as the company
                      name used in the URL.
                    </p>
                  </div>
                </InfoTooltip>
              </div>

              <Button
                size={Size.LG}
                onClick={oauthProcess}
                disabled={!company || !applicationType || !marketplace || !brand}
                loading={loading}
              >
                Submit
              </Button>
            </div>
          </WidgetContainer>
        </div>

        <div className='basic-card'>
          <div className='table-wrapper' style={{ height: '650px' }}>
            <ModalEditWidget<AmazonAccountRow>
              key={company}
              ref={widgetRef}
              className='retail-ops-accounts-table'
              headers={tableHeaders}
              initialTableData={accountRows || []}
              selectorOptions={{}}
              filterBar={false}
              enableAdd={false}
              onSaveModal={handleSaveSkaiPacvueIds}
              customEditor={{
                onRowClick: openEditModal,
                onAddClick: () => {}
              }}
              rowHeightGetter={estimateRowHeight}
              emptyContent={<NoAccountsRow />}
              isLoading={accountRowsLoading}
            />
          </div>
        </div>
      </div>
      {editingRow && (
        <EditSkaiPacvueIdsModal
          show={activeModal === RetailOpsModal.SKAI_PACVUE}
          onClose={() => {
            setActiveModal(undefined);
            setEditingRow(undefined);
          }}
          onSave={updated => {
            widgetRef.current?.trackModify(editingRow.rowKey, updated);
          }}
          row={editingRow}
          options={skaiPacvueOptions || []}
          optionsLoading={skaiPacvueOptionsLoading}
        />
      )}
      {editingRow && (
        <EditGrepsrCredentialsModal
          show={activeModal === RetailOpsModal.GREPSR}
          onClose={() => {
            setActiveModal(undefined);
            setEditingRow(undefined);
          }}
          onCreated={refetchAmazonAccountRows}
          company={company}
          row={editingRow}
        />
      )}
    </Page>
  );
};
